import {AccountPerformanceService} from "./AccountPerformanceService";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {IsoDate} from "../../pseudo-types/DateTypes";
import {Pair} from "../../types/GenericTypes";
import {PerformanceChartData, PerformanceChartDataPoint} from "../models/PerformanceChart";
import {TimeFrame, TimeFrameRange, TimeFrameRanges} from "../models/TimeFrame";
import {TimeFrameUtil} from "../util/TimeFrameUtil";
import {combineTrades} from "./AccountPerformanceViewStoreUtil";
import {DateTimeUtil} from "../../js/util/DateTimeUtil";
import {Optional} from "../../js/util/Optional";
import {isNotNull} from "../../js/util/UtilFunctions";
import {HistoricalTrades} from "../models/HistoricalTradeData";
import {AccountMarginSummary, AccountPerformanceSummary} from "../models/AccountPerformanceSummary";

export class AccountPerformanceViewStore {

    private readonly timeFrame$ = new BehaviorSubject(TimeFrame.YTD);
    private readonly timeFrameRanges$ = new ReplaySubject<TimeFrameRanges>(1);
    private readonly selectedTimeFrameRange$ = new ReplaySubject<TimeFrameRange | null>(1);
    private readonly dateRange$ = new ReplaySubject<Pair<IsoDate>>(1);

    private readonly currentPortfolio: Observable<number>;

    private readonly accountPerformance$ = new ReplaySubject<AccountPerformanceSummary>(1);
    private readonly tradeHistory$ = new ReplaySubject<HistoricalTrades>(1);
    private readonly trades$ = new ReplaySubject<HistoricalTrades>(1);
    private readonly chartData$ = new ReplaySubject<PerformanceChartData>(1);

    private readonly reload$ = new BehaviorSubject(true);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    constructor(portfolioId: number,
                private readonly accountPerformanceService: AccountPerformanceService) {
        this.currentPortfolio = Observable.of(portfolioId);
        this.init();
    }

    public init() {
        this.initTimeFrameRanges();
        this.initSelectedTimeFrameRange();
        this.initDateRangeChange();
        this.watchTimeFrame();
        this.initReload();
        this.initChartData();
        this.initTradeHistory();
        this.initTradeList();
    }

    private initTimeFrameRanges() {
        this.chartData$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .map((chartData) => this.dataDates(chartData))
            .map((dates) => TimeFrameUtil.timeFrameRanges(dates))
            .subscribe((ranges) => this.timeFrameRanges$.next(ranges));
    }

    private initSelectedTimeFrameRange() {
        const timeFrame$ = this.timeFrame$.distinctUntilChanged();
        const source$ = Observable.combineLatest(timeFrame$, this.timeFrameRanges$);
        source$.observeOn(Scheduler.async)
               .takeUntil(this.destroyed$)
               .map(([timeframe, ranges]) => ranges.find((r) => r.timeFrame === timeframe)!)
               .subscribe((range) => this.selectedTimeFrameRange$.next(range));
    }

    private dataDates(data: PerformanceChartData): ReadonlyArray<IsoDate> {
        return data.map((d) => d.asAt).map(DateTimeUtil.isoDate);
    }

    private initReload() {
        this.reload$
            .withLatestFrom(this.currentPortfolio)
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .switchMap(([_, portfolioId]) => this.retrieveAccountSummary(portfolioId))
            .subscribe((summary) => this.accountPerformance$.next(summary));
    }

    private retrieveAccountSummary(portfolioId: number): Observable<AccountPerformanceSummary> {
        return Observable.of(portfolioId)
                         .flatMap((pid) => this.accountPerformanceService.accountSummary(pid))
                         .catch((err) => Observable.empty<AccountPerformanceSummary>());
    }

    private initChartData() {
        this.accountPerformance$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .map((summary) => summary.marginHistory)
            .map((history) => history.map(this.chartDataPoint))
            .subscribe((data) => this.chartData$.next(data));
    }

    private chartDataPoint(marginSummary: AccountMarginSummary): PerformanceChartDataPoint {
        return {
            asAt: marginSummary.asAt,
            nlv: marginSummary.nlv
        };
    }

    private initDateRangeChange() {
        this.dateRange$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .distinctUntilChanged(this.dateRangesEqual)
            .withLatestFrom(this.timeFrame$)
            .filter(([_, timeFrame]) => timeFrame !== TimeFrame.CUSTOM)
            .map(([range]) => range)
            .withLatestFrom(this.timeFrameRanges$)
            .map(([range, timeframes]) => this.findMatchingTimeframe(range, timeframes))
            .map((range) => this.extractTimeFrame(range))
            // We only care to update from something that isn't custom to custom on date changes as this is just to
            // feed back into the UI
            .filter((timeframe) => timeframe === TimeFrame.CUSTOM)
            .subscribe((timeframe) => this.timeFrame$.next(timeframe));
    }

    private findMatchingTimeframe(range: Pair<IsoDate>,
                                  timeframes: ReadonlyArray<TimeFrameRange>): TimeFrameRange | undefined {
        return timeframes.find((tf) => tf.matches(range));
    }

    private extractTimeFrame(range?: TimeFrameRange): TimeFrame {
        return Optional.of(range).map((r) => r.timeFrame).orElse(TimeFrame.CUSTOM);
    }

    private watchTimeFrame() {
        this.timeFrame$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .distinctUntilChanged()
            .filter((tf) => tf !== TimeFrame.CUSTOM)
            .withLatestFrom(this.timeFrameRanges$)
            .map(([timeframe, ranges]) => this.findRangeForTimeframe(timeframe, ranges))
            .filter(isNotNull)
            .map((timeframe) => timeframe.range)
            .subscribe((range) => this.dateRange$.next(range));
    }

    private findRangeForTimeframe(timeframe: TimeFrame,
                                  ranges: ReadonlyArray<TimeFrameRange>): TimeFrameRange | undefined {
        return ranges.find((r) => r.timeFrame === timeframe);
    }

    private initTradeHistory() {
        this.accountPerformance
            .map(combineTrades)
            .takeUntil(this.destroyed$)
            .subscribe((trades) => this.tradeHistory$.next(trades));
    }

    private initTradeList() {
        Observable.combineLatest(this.tradeHistory$, this.dateRange)
                  .takeUntil(this.destroyed$)
                  .observeOn(Scheduler.async)
                  .map(([trades, range]) => this.filterTrades(trades, range))
                  .subscribe((trades) => this.trades$.next(trades));
    }

    private filterTrades(trades: HistoricalTrades,
                         range: Pair<IsoDate>): HistoricalTrades {
        const start = range[0];
        const end = range[1];

        return trades.filter((t) => {
            const tradeTime = t.tradeDate;
            const afterStart = tradeTime.localeCompare(start) >= 0;
            const beforeEnd = tradeTime.localeCompare(end) <= 0;
            return afterStart && beforeEnd;
        });
    }

    private reload() {
        this.reload$.next(true);
    }

    public updateTimeFrame(timeFrame: TimeFrame) {
        this.timeFrame$.next(timeFrame);
    }

    public updateDataRange(range: Pair<IsoDate>) {
        this.dateRange$.next(range);
    }

    public get tradeHistory(): Observable<HistoricalTrades> {
        return this.tradeHistory$.observeOn(Scheduler.async);
    }

    public get trades(): Observable<HistoricalTrades> {
        return this.trades$
                   .takeUntil(this.destroyed$)
                   .observeOn(Scheduler.async);
    }

    public get timeFrame(): Observable<TimeFrame> {
        return this.timeFrame$
                   .takeUntil(this.destroyed$)
                   .observeOn(Scheduler.async)
                   .distinctUntilChanged();
    }

    public get timeFrameRange(): Observable<TimeFrameRange | null> {
        return this.selectedTimeFrameRange$
                   .observeOn(Scheduler.async)
                   .takeUntil(this.destroyed$)
                   .distinctUntilChanged((a, b) => {
                       if ((a == null) || (b == null)) {
                           return a === b;
                       } else {
                           return a.isEqualTo(b);
                       }
                   });
    }

    public get dateRange(): Observable<Pair<IsoDate>> {
        return this.dateRange$
                   .takeUntil(this.destroyed$)
                   .observeOn(Scheduler.async)
                   .distinctUntilChanged(this.dateRangesEqual);
    }

    private dateRangesEqual(a: Pair<IsoDate>, b: Pair<IsoDate>): boolean {
        return (a[0] === b[0]) && (a[1] === b[1]);
    }

    public get accountPerformance(): Observable<AccountPerformanceSummary> {
        return this.accountPerformance$
                   .takeUntil(this.destroyed$)
                   .observeOn(Scheduler.async);
    }

    public get exportDataUrl(): Observable<string> {
        return Observable.combineLatest(this.currentPortfolio, this.dateRange)
                         .takeUntil(this.destroyed$)
                         .observeOn(Scheduler.async)
                         .map(([portfolioId, [from, to]]) => this.accountPerformanceService.calculateExportLink(
                             portfolioId,
                             from,
                             to));
    }

    public get chartData(): Observable<PerformanceChartData> {
        return this.chartData$
                   .takeUntil(this.destroyed$)
                   .observeOn(Scheduler.async);
    }

    public get timeFrameRanges(): Observable<TimeFrameRanges> {
        return this.timeFrameRanges$
                   .takeUntil(this.destroyed$)
                   .observeOn(Scheduler.async);
    }

    public destroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    public static initialize(currentPortfolioId: number,
                             accountPerformanceService: AccountPerformanceService) {
        return new AccountPerformanceViewStore(currentPortfolioId, accountPerformanceService);
    }
}
